/**
 * `/answer/questionnaires` — list + create (M-ANSWER.3).
 *
 * Quick-create form at top, list of existing questionnaires with status
 * badges below. Clicking a row drills into the detail/review UI.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAnswerQuestionnaires,
  useCreateAnswerQuestionnaire,
  useDeleteAnswerQuestionnaire,
} from "@/hooks/use-answer";
import { formatTimestamp } from "@/lib/formatters";

export function AnswerQuestionnairesPage() {
  const list = useAnswerQuestionnaires();
  const createMut = useCreateAnswerQuestionnaire();
  const deleteMut = useDeleteAnswerQuestionnaire();

  const [title, setTitle] = useState("");
  const [buyerName, setBuyerName] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    createMut.mutate(
      {
        title: title.trim(),
        ...(buyerName.trim() ? { buyer_name: buyerName.trim() } : {}),
      },
      {
        onSuccess: () => {
          setTitle("");
          setBuyerName("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Answer</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Questionnaires</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Create a questionnaire, import questions, generate draft responses with citations,
          then review and approve.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>New questionnaire</CardTitle>
          <CardDescription>
            Add a title + buyer name. You'll import questions on the detail page next.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Title (e.g. Acme Inc. SOC 2 questionnaire)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Buyer name (optional)"
              value={buyerName}
              onChange={(e) => setBuyerName(e.target.value)}
            />
            <div className="md:col-span-2">
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? "Creating…" : "Create"}
              </Button>
              {createMut.error && (
                <span className="ml-3 text-sm text-destructive">{createMut.error.message}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {list.isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load questionnaires: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No questionnaires yet</CardTitle>
            <CardDescription>
              Create your first questionnaire above, then import questions on the detail page.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2" data-testid="answer-questionnaires-list">
          {list.data.items.map((q) => (
            <li key={q.id}>
              <Card>
                <CardContent className="flex items-start justify-between gap-3 pt-6">
                  <Link
                    to={`/answer/questionnaires/${q.id}`}
                    className="min-w-0 flex-1 rounded outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`answer-questionnaire-row-link-${q.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{q.title}</span>
                      <Badge variant={q.status === "ready" || q.status === "completed" ? "default" : "outline"}>
                        {q.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {q.buyer_name ? `${q.buyer_name} · ` : ""}
                      created {formatTimestamp(q.created_at)}
                    </div>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMut.mutate(q.id)}
                    disabled={deleteMut.isPending}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
